import { useState, useEffect } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Sparkles, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

// sessionStorage (not localStorage) so the tip resets every browser session —
// important for public/shared computers where Grammarly must be re-installed.
// We default to COLLAPSED so the dashboard isn't dominated by an instructional card.
const STORAGE_KEY = "resumelock_grammarly_tip_open_v2";

export default function GrammarlyTip() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const v = sessionStorage.getItem(STORAGE_KEY);
      if (v === "1") setOpen(true);
    } catch (e) { /* ignore */ }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { sessionStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch (e) { /* ignore */ }
  };

  return (
    <Card
      className="mb-6 border-emerald-200 bg-emerald-50/50 overflow-hidden"
      data-testid="grammarly-tip"
    >
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left px-5 py-3 flex items-center justify-between gap-3 hover:bg-emerald-50 transition"
        data-testid="grammarly-tip-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display font-semibold text-emerald-900">
              Want extra help with your words?
            </div>
            {!open && (
              <div className="text-xs text-emerald-900/70">
                Get the free Grammarly extension. Click to see how.
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-emerald-900/70">
          <span className="text-xs uppercase tracking-wider font-mono-display hidden sm:inline">
            {open ? "Hide" : "Show"}
          </span>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1" data-testid="grammarly-tip-body">
          <div className="pl-12">
            <div className="text-sm text-emerald-900/80 leading-relaxed">
              Grammarly is a free tool. It checks your spelling, grammar, and how you write.
              It works inside our app.
            </div>
            <ol className="mt-3 text-sm text-emerald-900/90 list-decimal pl-5 space-y-1">
              <li>Open the <span className="font-medium">Chrome Web Store</span>. (Or <span className="font-medium">Add-ons</span> if you use Firefox or Edge.)</li>
              <li>Search for <span className="font-medium">Grammarly</span>.</li>
              <li>Click <span className="font-medium">Add to Chrome</span>.</li>
              <li>If a box pops up, click <span className="font-medium">Add extension</span>.</li>
              <li>Make a free Grammarly account when it asks.</li>
            </ol>
            <div className="mt-3 text-sm text-emerald-900/80">
              That's it. Now when you type in any box, look for a small <span className="font-semibold">green G</span>. It will show you what to fix.
            </div>
            <div className="mt-4">
              <a
                href="https://chromewebstore.google.com/detail/grammarly-ai-writing-and/kbfnbcaeplbcioakkpcpgfkobkghlhen"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="grammarly-install-btn"
                >
                  Get Grammarly for Chrome
                  <ExternalLink className="w-3 h-3 ml-1.5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
